import { useQuery, useQueryClient } from '@tanstack/react-query';
import { View } from 'react-native';
import { LogOut, Monitor, Smartphone } from 'lucide-react-native';
import { colors } from '@hogar/shared';
import { Card, Loading, RowLink, Screen, Txt, confirm, showProblem, useT } from '../components/ui';
import { BackHeader } from '../components/BackHeader';
import { api } from '../lib/api';
import { useSession } from '../lib/session';

interface Device {
  id: string;
  device: string;
  signedInAt: string;
  lastUsedAt: string;
  current: boolean;
}

/** The phones and computers signed in to this account; any one can be signed out (company standard, section 7). */
export default function Devices() {
  const { t, lang } = useT();
  const { signOut } = useSession();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['sessions'], queryFn: () => api<{ sessions: Device[] }>('GET', '/auth/sessions') });
  const when = (iso: string) => new Date(iso).toLocaleDateString(lang === 'en' ? 'en-US' : 'es-MX', { day: 'numeric', month: 'short', year: 'numeric' });

  const end = async (d: Device) => {
    if (!(await confirm(t('signOutDeviceConfirm'), t('signOutDevice'), t('cancel')))) return;
    try {
      await api('DELETE', `/auth/sessions/${d.id}`);
      if (d.current) return signOut();
      await qc.invalidateQueries({ queryKey: ['sessions'] });
    } catch {
      showProblem(t('errGeneric'), lang);
    }
  };

  return (
    <Screen>
      <BackHeader title={t('devices')} subtitle={t('devicesSub')} fallback="/mas" />
      {list.isPending ? (
        <Loading />
      ) : (
        <Card>
          {(list.data?.sessions ?? []).map((d, i, all) => (
            <View key={d.id}>
              <RowLink
                Icon={/iPhone|Android|iPad/.test(d.device) ? Smartphone : Monitor}
                title={`${d.device || t('unknownDevice')}${d.current ? ` · ${t('thisDevice')}` : ''}`}
                subtitle={t('lastUsed', { when: when(d.lastUsedAt) })}
                right={<View />}
                last
              />
              <RowLink Icon={LogOut} iconTint={colors.danger} iconBg={colors.dangerSoft} title={t('signOutDevice')} onPress={() => end(d)} last={i === all.length - 1} />
            </View>
          ))}
        </Card>
      )}
      {list.isError && <Txt color={colors.dangerText}>{t('errGeneric')}</Txt>}
    </Screen>
  );
}
